import React, { useState, useEffect } from 'react';
import { 
  LayoutGrid, Zap, BarChart3, ArrowRight, Mic, 
  Globe, Instagram, Facebook, Youtube, Twitter, 
  Linkedin, Github, Music, Share2, Shield, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { firebaseService } from '../services/firebaseService';
import { SocialLink } from '../types';

interface Props {
  onGetStarted: (mode?: 'login' | 'signup') => void;
}

const FALLBACK_VIDEOS = [{ url: '/assets/priori-ai-480p.mp4', label: 'Product Overview' }];

export const LandingPage: React.FC<Props> = ({ onGetStarted }) => {
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [videos, setVideos] = useState(FALLBACK_VIDEOS);

  useEffect(() => {
    const loadSocial = async () => {
      const active = await firebaseService.getSocialLinks(true);
      setSocialLinks(active);
    };
    const loadVideos = async () => {
      const v = await firebaseService.getLandingVideos();
      if (v.length > 0) setVideos(v.map(v => ({ url: v.url, label: v.label })));
    };
    loadSocial();
    loadVideos();
  }, []);

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram': return <Instagram size={20} />;
      case 'facebook': return <Facebook size={20} />;
      case 'youtube': return <Youtube size={20} />;
      case 'x': return <Twitter size={20} />;
      case 'linkedin': return <Linkedin size={20} />;
      case 'github': return <Github size={20} />;
      case 'tiktok': return <Music size={20} />;
      default: return <Globe size={20} />;
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-blue-100">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 px-6 py-4 flex justify-between items-center transition-all">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-white shadow-sm">P</div>
          <span className="font-bold text-lg tracking-tight text-slate-900">Priori</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => onGetStarted('login')} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors px-4 py-2">Log In</button>
          <button 
            onClick={() => onGetStarted('signup')}
            className="text-sm font-medium bg-slate-900 text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-all shadow-sm active:scale-95"
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
          {/* Left: copy + CTAs */}
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-8 fade-in-up">
              <Zap size={14} className="fill-blue-700" /> <span>Intelligent Task Management</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6 text-slate-900 fade-in-up">
              Prioritize what <br />
              <span className="text-slate-400">actually matters.</span>
            </h1>

            <p className="text-lg text-slate-600 max-w-xl mb-10 leading-relaxed fade-in-up">
              Stop getting lost in the noise. Priori helps you classify tasks using the Eisenhower Matrix so you can focus on high-impact work.
            </p>

            <div className="flex flex-col sm:flex-row items-center md:items-start justify-center md:justify-start gap-3 fade-in-up">
              <button
                onClick={() => onGetStarted('signup')}
                className="w-full sm:w-auto px-8 py-3.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                Start for Free <ArrowRight size={16} />
              </button>
              <button
                onClick={() => onGetStarted('login')}
                className="w-full sm:w-auto px-8 py-3.5 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-all"
              >
                Log In
              </button>
            </div>
          </div>

          {/* Right: app screenshot */}
          <div className="flex-1 flex justify-center md:justify-end fade-in-up">
            <img
              src="/assets/Priori-front.png"
              alt="Priori app — Eisenhower Matrix view"
              className="w-full max-w-sm md:max-w-md rounded-2xl shadow-2xl border border-slate-100"
            />
          </div>
        </div>
      </section>

      {/* Video Demo Carousel */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="px-6 mb-8 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">See Priori in action</h2>
            <p className="text-slate-500 text-sm">Watch how Priori helps you cut through the noise and execute on what matters.</p>
          </div>
          <div className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory px-6 pb-4 scrollbar-hide">
            {videos.map((video, i) => (
              <div
                key={i}
                className="snap-start shrink-0 w-72 rounded-2xl overflow-hidden border border-slate-200 shadow-md bg-white"
              >
                <video
                  src={video.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full"
                />
                <div className="px-4 py-3 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-600">{video.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                <LayoutGrid size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Eisenhower Matrix</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Automatically categorize tasks into Do, Schedule, Delegate, and Delete. Visualize your priorities instantly without the clutter.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center mb-6">
                <Mic size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Voice Capture</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Speak your mind. Our AI assistant transcribes and organizes your thoughts into actionable tasks automatically.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center mb-6">
                <BarChart3 size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Progress Tracking</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Review your productivity trends. Understand where your time goes and optimize your workflow for high-leverage activities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Socials */}
      {socialLinks.length > 0 && (
        <section className="py-24 px-6 border-t border-slate-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-12 tracking-tight">Connect With Us</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {socialLinks.map((social) => (
                <a 
                  key={social.id}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all group"
                >
                  <div className="text-slate-500 group-hover:text-slate-900 transition-colors">
                    {getPlatformIcon(social.platform)}
                  </div>
                  <span className="font-medium text-sm text-slate-600 group-hover:text-slate-900">{social.label}</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
           <div className="flex items-center gap-2">
             <div className="w-6 h-6 bg-slate-900 rounded-md flex items-center justify-center font-bold text-white text-xs">P</div>
             <span className="font-semibold text-slate-900">Priori</span>
           </div>
           
           <div className="flex gap-8 text-sm font-medium text-slate-500">
              <Link to="/trust" className="hover:text-slate-900 transition-colors">Trust Center</Link>
              <Link to="/login" onClick={(e) => { e.preventDefault(); onGetStarted('login'); }} className="hover:text-slate-900 transition-colors">Log In</Link>
           </div>
           
           <div className="text-sm text-slate-400">
             &copy; {new Date().getFullYear()} Priori. All rights reserved.
           </div>
        </div>
      </footer>
    </div>
  );
};